import { Clock } from 'lucide-react'
import Paper from '@/components/Paper'
import { DashboardOverview } from '@/modules/Service/services/sla'
import {
    SLAComplianceChart,
    DowntimeBreakdownChart,
    UptimeGaugeChart,
} from '../charts'
import { formatMinutes } from '../../Shared/utils/format-value'

interface OverviewChartsProps {
    overview: DashboardOverview
    periodLabel: string
}

const OverviewCharts = ({ overview, periodLabel }: OverviewChartsProps) => {
    const totalDowntime =
        (overview.total_provider_downtime || 0) +
        (overview.total_third_party_downtime || 0) +
        (overview.total_scheduled_downtime || 0) +
        (overview.total_customer_downtime || 0)
    const hasDowntime = totalDowntime > 0 || (overview.total_incidents || 0) > 0

    return (
        <div className="space-y-2">
            {/* Charts row — Phân Bố Downtime hidden when no data to reclaim space */}
            <div
                className={`grid grid-cols-1 gap-2 ${
                    hasDowntime ? 'md:grid-cols-3' : 'md:grid-cols-2'
                }`}
            >
                <Paper className="p-4">
                    <div className="pb-2">
                        <p className="text-sm font-medium">Uptime Trung Bình</p>
                        <p className="text-xs text-gray-500">{periodLabel}</p>
                    </div>
                    <UptimeGaugeChart
                        uptimePercent={overview.avg_uptime_percent}
                    />
                </Paper>

                <Paper className="p-4">
                    <div className="pb-2">
                        <p className="text-sm font-medium">Tuân Thủ SLA</p>
                        <p className="text-xs text-gray-500">{periodLabel}</p>
                    </div>
                    <SLAComplianceChart
                        meetingSLA={overview.services_meeting_sla}
                        breachingSLA={overview.services_breaching_sla}
                    />
                </Paper>

                {hasDowntime && (
                    <Paper className="p-4">
                        <div className="pb-2">
                            <p className="text-sm font-medium">
                                Phân Bố Downtime · {periodLabel}
                            </p>
                        </div>
                        <DowntimeBreakdownChart
                            providerDowntime={overview.total_provider_downtime}
                            thirdPartyDowntime={
                                overview.total_third_party_downtime
                            }
                            scheduledDowntime={
                                overview.total_scheduled_downtime
                            }
                            customerDowntime={overview.total_customer_downtime}
                        />
                    </Paper>
                )}
            </div>

            {/* Downtime stats grid — only when there is actual downtime */}
            {hasDowntime && (
                <Paper className="p-4">
                    <div className="pb-3">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-orange-500" />
                            <p className="text-sm font-medium">
                                Thống Kê Downtime
                            </p>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                            Chi tiết thời gian downtime theo nguyên nhân ·{' '}
                            {periodLabel}
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className="p-3 border border-gray-100 rounded-sm text-center">
                            <div className="text-xl font-semibold text-foreground">
                                {formatMinutes(
                                    overview.total_sla_downtime_minutes
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Downtime tính SLA
                            </div>
                            <div className="text-[11px] text-muted-foreground/70 mt-1">
                                = Provider + Third-party + Bảo trì
                            </div>
                        </div>
                        <div className="p-3 border border-gray-100 rounded-sm text-center">
                            <div className="text-xl font-semibold text-foreground">
                                {formatMinutes(
                                    overview.total_provider_downtime
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Provider
                            </div>
                        </div>
                        <div className="p-3 border border-gray-100 rounded-sm text-center">
                            <div className="text-xl font-semibold text-foreground">
                                {formatMinutes(
                                    overview.total_third_party_downtime
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Third-party
                            </div>
                        </div>
                        <div className="p-3 border border-gray-100 rounded-sm text-center">
                            <div className="text-xl font-semibold text-foreground">
                                {formatMinutes(
                                    overview.total_scheduled_downtime
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Bảo trì
                            </div>
                        </div>
                        <div className="p-3 border border-gray-100 rounded-sm text-center">
                            <div className="text-xl font-semibold text-foreground">
                                {overview.total_incidents}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Sự cố
                            </div>
                        </div>
                    </div>
                </Paper>
            )}
        </div>
    )
}

export default OverviewCharts
